<template>
  <div class="bills-status-widget-container">
    <div v-if="loading" class="bills-status-widget-loading">Loading...</div>
    <div v-else-if="error" class="bills-status-widget-error">{{ error }}</div>
    <div v-else class="bills-status-widget-content">
      <div class="bills-status-widget-header">
        <div class="bills-status-widget-header-label">Bills Status</div>
      </div>
      <div class="bills-status-widget-details">
        <div class="bills-status-widget-detail-row">
          <div class="bills-status-widget-detail-label">
            <span class="bills-status-widget-badge bills-status-widget-open"></span>
            Open Bills
          </div>
          <div class="bills-status-widget-detail-value">
            {{ openBillsCount }}
          </div>
        </div>
        <div class="bills-status-widget-detail-row">
          <div class="bills-status-widget-detail-label">
            <span class="bills-status-widget-badge bills-status-widget-overdue"></span>
            Overdue Bills
          </div>
          <div class="bills-status-widget-detail-value bills-status-widget-overdue-value">
            {{ overdueBillsCount }}
          </div>
        </div>
        <div class="bills-status-widget-detail-row">
          <div class="bills-status-widget-detail-label">Total Amount Due</div>
          <div class="bills-status-widget-detail-value bills-status-widget-amount">{{ formatCurrency(totalAmountDue) }}</div>
        </div>
      </div>
      <div v-if="openBillsCount > 0 || overdueBillsCount > 0" class="bills-status-widget-footer">
        <router-link :to="{ path: `/company/${companyId}/dashboard/vendors/bills`, query: { view: 'Bills' } }" class="bills-status-widget-link">View all bills →</router-link>
      </div>
    </div>
  </div>
</template>

<script>
import './styles/BillsStatusWidget.css';
import ApiService from '@/services/api.service';
import logger from '@/utils/logger';

export default {
  name: 'BillsStatusWidget',
  props: {
    companyId: { type: [String, Number], required: true }
  },
  data() {
    return {
      openBillsCount: 0,
      overdueBillsCount: 0,
      totalAmountDue: 0,
      loading: true,
      error: null
    };
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      this.error = null;
      try {
        const bills = await ApiService.getBillsAndExpenses(this.companyId, { view: 'Bills' });
        const billsArray = Array.isArray(bills) ? bills : [];
        
        // Filter out cancelled/voided and fully paid bills
        // Keep bills that have a balance due (not fully paid)
        const validBills = billsArray.filter(bill => {
          const status = (bill.status || '').toLowerCase();
          const balance = parseFloat(bill.balance || bill.remainingAmount || 0);
          const voided = bill.voided || bill.isVoided || status === 'cancelled';
          
          // Exclude voided/cancelled and fully paid bills
          if (voided || status === 'paid') {
            return false;
          }
          
          // Only include bills with a balance due
          return balance > 0;
        });
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // First, determine which bills are overdue
        // Use a unique identifier for each bill (id, billId, or combination of billNo and vendorId)
        const getBillKey = (bill) => {
          return bill.id || bill.billId || `${bill.billNo || ''}_${bill.vendorId || ''}`;
        };
        
        const overdueBillsSet = new Set();
        validBills.forEach(bill => {
          const status = (bill.status || '').toLowerCase();
          const dueDate = bill.dueDate ? new Date(bill.dueDate) : null;
          const billKey = getBillKey(bill);
          
          // Explicitly marked as overdue
          if (status === 'overdue') {
            overdueBillsSet.add(billKey);
            return;
          }
          
          // Check if past due date (for any bill with a due date)
          if (dueDate && (status === 'open' || status === 'partial' || status === 'disputed' || status === 'on hold')) {
            const dueDateObj = new Date(dueDate);
            dueDateObj.setHours(0, 0, 0, 0);
            if (dueDateObj < today) {
              overdueBillsSet.add(billKey);
            }
          }
        });
        
        // Count overdue bills
        this.overdueBillsCount = overdueBillsSet.size;
        
        // Count open bills (any bill with balance that is not overdue, not paid, not cancelled)
        this.openBillsCount = validBills.filter(bill => {
          const billKey = getBillKey(bill);
          const status = (bill.status || '').toLowerCase();
          
          // Skip if overdue
          if (overdueBillsSet.has(billKey)) {
            return false;
          }
          
          // Skip if explicitly marked as paid or cancelled
          if (status === 'paid' || status === 'cancelled') {
            return false;
          }
          
          // Open bills are: Open, Partial, Disputed, On Hold (any unpaid status)
          // Since we already filtered validBills to only include bills with balance > 0,
          // any bill that's not overdue, not paid, and not cancelled should be considered open
          return true;
        }).length;
        
        // Calculate total amount due (all valid bills with balance)
        this.totalAmountDue = validBills.reduce((sum, bill) => {
          return sum + parseFloat(bill.balance || bill.remainingAmount || 0);
        }, 0);
      } catch (error) {
        logger.error('Error fetching bills status:', error);
        this.error = 'Failed to load data';
      } finally {
        this.loading = false;
      }
    },
    formatCurrency(amount) {
      if (amount === null || amount === undefined || isNaN(amount)) return '$0.00';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    }
  }
};
</script>

